import { useState, useEffect, useRef } from 'react';
import { Menu, Clipboard, X, ChevronDown, Globe, MessageSquare, BookOpen, Lock, AlertTriangle, Battery } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGetProfile } from './hooks/useProfile.ts';
import Logo from './components/Logo.tsx';
import { useQueryClient } from '@tanstack/react-query';
import Select from './components/ui/Select.tsx';
import { useSwitchGroup } from './hooks/useLogin.ts';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import api from './utils/axios.ts';
import Modal from './components/Modal.tsx';
import { useIsAdmin, useIsGroupAdmin } from './hooks/useIsAdmin.ts';
import OnboardingModal from './components/OnboardingModal';
import { useFeatures } from './hooks/useFeatures.ts';
import { usePlanDurations } from './hooks/usePlanDurations.ts';
import { useGroupFeatures } from './hooks/useGroupFeatures.ts';
import { navigationFeatures } from './utils/featureConfig.js';
import { useContextSwitch } from './hooks/useContextSwitch.ts';
import { useLayoutType } from './hooks/useLayoutType.ts';
import { useSmsQuota } from './hooks/useSmsQuota';
import { ThemeToggle } from './components/ThemeToggle.tsx';
import NotificationBell from './components/NotificationBell';
import FeatureAnnouncementBanner from './components/FeatureAnnouncementBanner';

const PRICING_ANNOUNCEMENT_ID = 'advanced-plan-price-update-2026-09-v2';
// Fallback used only when the backend hasn't returned a renewal/upgrade amount for the current (non-Advanced) plan.
const DEFAULT_CORE_PLAN_AMOUNT = 250;

export default function Layout({ children }) {
  const [activePage, setActivePage] = useState('dashboard');
  const location = useLocation();

  useEffect(() => {
    setActivePage(location.pathname);
  }, [location.pathname]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Lock page scroll behind the mobile drawer so swiping scrolls the menu, not the page
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('mobile-menu-open');
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);
  const [loansDropdownOpen, setLoansDropdownOpen] = useState(false);
  const [contributionsDropdownOpen, setContributionsDropdownOpen] = useState(false);
  const [meetingsDropdownOpen, setMeetingsDropdownOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const toggleDropdown = (code) => {
    setOpenDropdowns(prev => ({ [code]: !prev[code] }));
  };
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [isPricingBannerDismissed, setIsPricingBannerDismissed] = useState(() => {
    return localStorage.getItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`) === 'true';
  });
  const handleDismissPricingBanner = () => {
    localStorage.setItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`, 'true');
    setIsPricingBannerDismissed(true);
  };
  const { data: profile, isLoading: isProfileLoading, refetch: refetchProfile } = useGetProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const switchGroup = useSwitchGroup();
  const { features: availableFeatures, upgradeAmount, renewalAmount, currentPlanId, currentPlanName, advancedPlanId, isLoading: isFeaturesLoading } = useFeatures();
  const { durations } = usePlanDurations();
  const { selectedFeatures: groupSelectedFeatures } = useGroupFeatures();
  const isAdmin = useIsAdmin();
  const isGroupAdmin = useIsGroupAdmin();
  const { switchContext, isSwitching } = useContextSwitch();
  const layoutType = useLayoutType();
  const { data: quotaData } = useSmsQuota();

  // Feature protection logic - hide admin features for non-admins, lock unavailable plan features
  const isFeatureLocked = (feature) => {
    if (feature.alwaysShow) return false;
    if (isFeaturesLoading) return false; // Don't lock features while loading
    if (feature.apiFeature && !availableFeatures.includes(feature.apiFeature)) return true;
    return false;
  };
  
  const userRole = profile?.data?.current_role || profile?.data?.role?.name || profile?.data?.user?.role?.name;
  const isVillageAdmin = userRole === 'village_admin' && !isAdmin && !isGroupAdmin;

  const shouldShowFeature = (feature) => {
    if (isVillageAdmin) {
      // Village Admin is restricted from viewing group admin configuration (admin management, disbursement, fines, expenses)
      // but can access settings (Profile tab) just like a normal member.
      if (['admin-management', 'disbursement', 'fines', 'expenses'].includes(feature.code)) {
        return false;
      }
    }
    if (feature.alwaysShow) return true;
    if (feature.adminOnly && !isAdmin) return false;
    if (feature.groupAdminOnly && !isGroupAdmin) return false;
    // if (feature.memberOnly && isAdmin) return false;
    
    // Admin-only features should still respect group selected features
    
    // Check if feature is selected by the group (if it has an apiFeature)
    // Only filter by groupSelectedFeatures if it's not empty
    // If empty, show all features (they'll be locked if not in availableFeatures)
    if (feature.apiFeature && groupSelectedFeatures.length > 0) {
      return groupSelectedFeatures.includes(feature.apiFeature);
    }
    
    // If no groupSelectedFeatures or feature doesn't have apiFeature, show it
    // (it will be locked if not available in the plan)
    return true;
  };
  
  // Get features by section (filter out hidden features)
  const getFeaturesBySection = (section) => {
    const isHouseManagement = Number(profile?.data?.current_group?.is_house_management) === 1;
    const isIrregular = Number(profile?.data?.current_group?.is_irregular) === 1;
    const houseLabel = profile?.data?.current_group?.house_label || 'House';
    const hasSubgroup = !!(profile?.data?.member?.subgroup_id);

    return navigationFeatures
      .filter(feature => {
        if (feature.code === 'houses') {
          return isHouseManagement && isAdmin;
        }
        if (feature.code === 'my-house') {
          return isHouseManagement && !isAdmin && hasSubgroup;
        }
        if (feature.code === 'subgroups') {
          return isIrregular && !isHouseManagement && isAdmin;
        }
        if (feature.code === 'my-subgroup') {
          return isIrregular && !isHouseManagement && !isAdmin && hasSubgroup;
        }
        return true;
      })
      .map(feature => {
        if (feature.code === 'houses') {
          return { ...feature, label: `${houseLabel}s` };
        }
        if (feature.code === 'my-house') {
          return { ...feature, label: `My ${houseLabel}` };
        }
        if (feature.code === 'subgroups') {
          return { ...feature, label: 'Subgroups' };
        }
        if (feature.code === 'my-subgroup') {
          return { ...feature, label: 'My Subgroup' };
        }
        return feature;
      })
      .filter(feature => 
        feature.section === section && shouldShowFeature(feature)
      );
  };
  
  const mainFeatures = getFeaturesBySection('main');
  const loanFeatures = getFeaturesBySection('loans');
  const contributionFeatures = getFeaturesBySection('contributions');
  const meetingFeatures = getFeaturesBySection('meetings');
  const adminFeatures = getFeaturesBySection('admin');
  const settingsFeatures = getFeaturesBySection('settings');
  


  // Banner close state with localStorage persistence
  const [showBanner, setShowBanner] = useState(() => {
    return localStorage.getItem('hideMpesaBanner') !== 'true';
  });

  useEffect(() => {
    if (!showBanner) {
      localStorage.setItem('hideMpesaBanner', 'true');
    }
  }, [showBanner]);

  // Check if SMS quota reminder should be shown
  const quota = quotaData?.data;
  // TEMPORARY: Hardcode is_low for testing - remove this after testing
  const testQuota = quota ? { ...quota, is_low: false } : null;
  const isExhausted = (testQuota || quota)?.is_exhausted || (testQuota || quota)?.remaining === 0;
  
  // SMS Quota reminder state with localStorage persistence
  const [showSmsQuotaReminder, setShowSmsQuotaReminder] = useState(() => {
    // Check if reminder was dismissed recently
    const dismissedAt = localStorage.getItem('smsQuotaReminderDismissedAt');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);
      
      // Check if it was dismissed for exhausted quota (8 hours) or low quota (24 hours)
      const wasExhaustedWhenDismissed = localStorage.getItem('smsQuotaReminderWasExhausted') === 'true';
      const waitTime = wasExhaustedWhenDismissed ? 8 : 24; // 8 hours for exhausted, 24 hours for low
      
      return hoursSinceDismissed >= waitTime;
    }
    return true;
  });

  // Update reminder visibility when quota status changes
  useEffect(() => {
    // Check dismissal state for both exhausted and low quota
    const dismissedAt = localStorage.getItem('smsQuotaReminderDismissedAt');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);
      const wasExhaustedWhenDismissed = localStorage.getItem('smsQuotaReminderWasExhausted') === 'true';
      const waitTime = wasExhaustedWhenDismissed ? 8 : 24; // 8 hours for exhausted, 24 hours for low
      
      // Show if enough time has passed OR if quota status changed (exhausted -> low or vice versa)
      const currentIsExhausted = isExhausted;
      const statusChanged = wasExhaustedWhenDismissed !== currentIsExhausted;
      
      setShowSmsQuotaReminder(hoursSinceDismissed >= waitTime || statusChanged);
    } else {
      // No dismissal recorded, show the reminder
      setShowSmsQuotaReminder(true);
    }
  }, [isExhausted, quota]);

  const handleDismissSmsQuotaReminder = () => {
    setShowSmsQuotaReminder(false);
    localStorage.setItem('smsQuotaReminderDismissedAt', Date.now().toString());
    // Store whether it was exhausted when dismissed
    localStorage.setItem('smsQuotaReminderWasExhausted', isExhausted ? 'true' : 'false');
  };

  // Show reminder if quota is low/exhausted AND dismissal time has passed (8 hours for exhausted, 24 hours for low)
  const shouldShowSmsQuotaReminder = isAdmin && 
    (testQuota || quota) && 
    ((testQuota?.is_low || quota?.is_low) || isExhausted) &&
    showSmsQuotaReminder;

  // Check if onboarding modal should be shown
  useEffect(() => {
    if (!isProfileLoading && profile?.data?.current_group) {
      const onboardingCompleted = localStorage.getItem('onboardingCompleted');

      // Show onboarding if not completed before (first time user)
      if (!onboardingCompleted) {
        setShowOnboardingModal(true);
      }
    }
  }, [isProfileLoading, profile]);

  const handleLogout = () => {
    // Clear auth token
    localStorage.removeItem('userToken');
    // Clear all queries from cache
    queryClient.clear();
    // Redirect to login
    navigate('/login');
  };

   const handleSwitchGroupSubmit = (groupId) => {
    switchGroup.mutate(
      { group_id: groupId },
      {
        onSuccess: () => {
          toast.success(
            'Group changed successfully'
          );
          setTimeout(function(){
            window.location.replace('/');
          }, 300);
        },
        onError: (error) => {
          // Try to read a message from the API response; fall back if missing
          const apiMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to change group. Please try again.';
          toast.error(apiMessage);
        },
      }
    );
  };

  // Create Group Modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState('');

  // Onboarding modal state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  useEffect(() => {
    if (location.search.includes('upgrade=true')) {
       setShowUpgradeModal(true);
       setUpgradeTargetPlan('advanced');
       window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search]);

  // Which plan the group is paying to move to: 'current' (renew the plan they're already
  // on, e.g. Core at renewalAmount) or 'advanced' (upgrade to Advanced at upgradeAmount).
  // Irrelevant once the group is already on Advanced - there's only one target then.
  const [upgradeTargetPlan, setUpgradeTargetPlan] = useState('advanced');
  const isOnAdvanced = currentPlanId != null && advancedPlanId != null && currentPlanId === advancedPlanId;
  const targetPlanId = isOnAdvanced ? advancedPlanId : (upgradeTargetPlan === 'advanced' ? advancedPlanId : currentPlanId);
  const targetBaseAmount = isOnAdvanced ? upgradeAmount : (upgradeTargetPlan === 'advanced' ? upgradeAmount : renewalAmount);

  // Once a trial/subscription has expired, the backend already rejects writes
  // (EnforceTrialReadOnly, code TRIAL_EXPIRED) - this blocks the UI itself so
  // the group can't sit on a page (e.g. Settings > Features) that looks fully
  // usable but silently fails on every action.
  const subscriptionExpiryDate = profile?.data?.current_group?.subscription_expiry_date;
  const isSubscriptionExpired = Boolean(subscriptionExpiryDate) && new Date(subscriptionExpiryDate) < new Date();
  const [upgradeForm, setUpgradeForm] = useState({
    phone_number: '',
    amount: DEFAULT_CORE_PLAN_AMOUNT,
    description: '',
    duration_months: 1
  });
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeStatus, setUpgradeStatus] = useState('idle'); // idle | processing | success | failed
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const pollingRef = useRef(null);
  const [upgradeSteps, setUpgradeSteps] = useState([
    { key: 'twachanga_start', label: 'Twachanga', status: 'idle' },
    { key: 'mpesa_init', label: 'M-Pesa', status: 'idle' },
    { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'idle' },
    { key: 'twachanga_return', label: 'Twachanga', status: 'idle' },
    { key: 'outcome', label: 'Success / Failed', status: 'idle' },
  ]);

  // Sync amount from backend whenever the selected target plan's price changes (fallback to DEFAULT_CORE_PLAN_AMOUNT)
  useEffect(() => {
    if (typeof targetBaseAmount !== 'undefined') {
      setUpgradeForm((prev) => ({
        ...prev,
        duration_months: prev.duration_months || 1,
        amount: (targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT) * (prev.duration_months || 1),
      }));
    }
  }, [targetBaseAmount]);

  const groupMutation = useMutation({
    mutationFn: async (payload) => {
      setGroupLoading(true);
      setGroupError('');
      const { data } = await api.post('/groups', payload);
      return data;
    },
    onSuccess: () => {
      setGroupLoading(false);
      setShowGroupModal(false);
      setGroupForm({ name: '', description: '' });
      alert('Group created! You can switch to the group using the dropdown on the navbar above.');
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      setGroupError(error?.response?.data?.message || 'Failed to create group');
      setGroupLoading(false);
    }
  });

  const handleGroupChange = (e) => {
    const { name, value } = e.target;
    setGroupForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGroupSubmit = (e) => {
    e.preventDefault();
    groupMutation.mutate({
      name: groupForm.name,
      description: groupForm.description
    });
  };

  // Handle feature actions
  const handleFeatureClick = (feature) => {
    if (feature.isAction) {
      if (feature.code === 'logout') {
        handleLogout();
      } else if (feature.code === 'context-switch') {
        switchContext.mutate(layoutType === 'admin' ? 'member' : 'admin');
      }
      return;
    }
    
    // Check if feature is locked
    if (isFeatureLocked(feature)) {
      toast.error(`This feature requires a higher plan. Please upgrade to access ${feature.label}.`);
      return;
    }
    
    setActivePage(feature.path);
    setIsMobileMenuOpen(false);
  };

  // Tooltip handlers
  const showTooltip = (text, event) => {
    setTooltip({
      show: true,
      text,
      x: event.clientX + 10,
      y: event.clientY - 10
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  };

  // Render feature link with lock indicator
  const renderFeatureLink = (feature) => {
    const isLocked = isFeatureLocked(feature);
    const isActive = activePage === feature.path;
    const isLoading = isFeaturesLoading && feature.apiFeature; // Show loading state for features that depend on API
    
    const baseClasses = `flex items-center w-full p-2 rounded-md transition-colors ${
      isActive 
        ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white' 
        : isLocked 
          ? 'text-gray-400 cursor-not-allowed dark:text-gray-500' 
          : isLoading
            ? 'text-gray-500 opacity-75 dark:text-gray-400'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
    }`;

    if (isLocked) {
      return (
        <div 
          className={baseClasses} 
          onClick={() => toast.error(`Upgrade your plan to unlock ${feature.label}.`)}
          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
          onMouseLeave={hideTooltip}
        >
          <feature.icon size={18} className="mr-2" />
          <span className="flex-1">{feature.label}</span>
          <Lock size={14} className="text-gray-400" />
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className={baseClasses} title="Loading feature availability...">
          <feature.icon size={18} className="mr-2" />
          <span className="flex-1">{feature.label}</span>
          <div className="w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
        </div>
      );
    }

    return (
      <Link 
        to={feature.path}
        className={baseClasses}
        onClick={() => handleFeatureClick(feature)}
      >
        <feature.icon size={18} className="mr-2" />
        {feature.label}
      </Link>
    );
  };

  // Render feature button (for actions like logout)
  const renderFeatureButton = (feature) => {
    const isLocked = isFeatureLocked(feature);
    const isLoading = isFeaturesLoading && feature.apiFeature;
    
    const baseClasses = `flex items-center w-full p-2 rounded-md ${
      isLocked 
        ? 'text-gray-400 cursor-not-allowed' 
        : isLoading
          ? 'text-gray-500 opacity-75'
          : 'text-gray-600 hover:bg-gray-100'
    }`;

    if (isLocked) {
      return (
        <button 
          className={baseClasses}
          disabled
          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
          onMouseLeave={hideTooltip}
        >
          <feature.icon size={18} className="mr-2" />
          <span className="flex-1">{feature.label}</span>
          <Lock size={14} className="text-gray-400" />
        </button>
      );
    }

    if (isLoading) {
      return (
        <button 
          className={baseClasses}
          disabled
          title="Loading feature availability..."
        >
          <feature.icon size={18} className="mr-2" />
          <span className="flex-1">{feature.label}</span>
          <div className="w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
        </button>
      );
    }

    // Special handling for context switch button
    if (feature.code === 'context-switch') {
      const switchLabel = layoutType === 'admin' ? 'Switch to Member' : 'Switch to Admin';
      const isSwitchingContext = isSwitching;
      
      return (
        <button 
          className={`${baseClasses} ${isSwitchingContext ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={() => handleFeatureClick(feature)}
          disabled={isSwitchingContext}
        >
          <feature.icon size={18} className="mr-2" />
          {isSwitchingContext ? 'Switching...' : switchLabel}
        </button>
      );
    }

    return (
      <button 
        className={baseClasses}
        onClick={() => handleFeatureClick(feature)}
      >
        <feature.icon size={18} className="mr-2" />
        {feature.label}
      </button>
    );
  };

  // Show loading state while determining layout type
  if (layoutType === 'loading') {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex relative h-screen bg-gray-100">
      {/* Custom Tooltip */}
      {tooltip.show && (
        <div 
          className="fixed z-[9999] px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg pointer-events-none"
          style={{ 
            left: tooltip.x, 
            top: tooltip.y,
            maxWidth: '300px'
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`absolute lg:static inset-y-0 left-0 z-50 h-[100dvh] lg:h-screen overflow-y-auto overscroll-contain bg-white shadow-lg pl-4 transform transition-transform duration-200 ease-in-out ${
        isMobileMenuOpen ? 'w-72 max-w-[85vw] translate-x-0' : 'w-0 -translate-x-full'
      } lg:w-64 lg:max-w-none lg:translate-x-0`}>
        <div className="flex sticky top-0 z-50 justify-between items-center px-4 py-5 bg-white border-b">
          <Link to='/' className="max-w-fit">
            <Logo />
          </Link>
          <button 
            className="p-2 rounded-md lg:hidden hover:bg-gray-100"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Plan Indicator - Only show for admins */}
        {!isFeaturesLoading && isAdmin && (
          <div className="p-2 mr-4 bg-gray-50 dark:bg-gray-800 rounded-xl border-b">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Current Plan</div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex gap-2 items-center">
                    <span>{currentPlanName ? `${currentPlanName} Plan` : 'Plan'}</span>
                    {profile?.data?.current_group?.is_trial && currentPlanName !== 'Advanced' && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-[#c2410c] bg-[#ffedd5] rounded-full dark:bg-[#7c2d12] dark:text-[#fdba74]">
                        <span className="mr-1 h-1.5 w-1.5 rounded-full bg-orange-500" />
                        Trial
                      </span>
                    )}
                  </div>
                </div>
                {profile?.data?.current_group?.subscription_expiry_date && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {(() => {
                      const expiryDate = new Date(profile.data.current_group.subscription_expiry_date);
                      const now = new Date();
                      const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

                      const timeLabel = daysRemaining > 0
                        ? `${daysRemaining} day${daysRemaining > 1 ? 's' : ''} remaining`
                        : daysRemaining === 0
                          ? 'Expires today'
                          : 'Expired';

                      return !isOnAdvanced && renewalAmount
                        ? `${timeLabel} · Renew for KES ${renewalAmount}/mo`
                        : timeLabel;
                    })()}
                  </div>
                )}
              </div>
                <button
                  onClick={() => {
                    setUpgradeTargetPlan('current');
                    setShowUpgradeModal(true);
                  }}
                  className="px-3 py-1 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-sm transition-all duration-200 hover:from-blue-700 hover:to-purple-700"
                >
                  {!isOnAdvanced ? "Upgrade" : "Extend" }
                </button>
            </div>
          </div>
        )}
        
        <div className="relative pr-4 pb-24 lg:pb-4">
          {/* Loading overlay for features */}
          {isFeaturesLoading && (
            <div className="flex absolute inset-0 z-10 justify-center items-center bg-white bg-opacity-75">
              <div className="text-center">
                <div className="mx-auto mb-2 w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                <p className="text-sm text-gray-600">Loading features...</p>
              </div>
            </div>
          )}
          
          {/* Main Features */}
          <div>
            <p className="mt-3 mb-2 text-xs font-bold text-gray-500 uppercase">Main</p>
            {mainFeatures.map(feature => (
              <div key={feature.code}>
                {renderFeatureLink(feature)}
              </div>
            ))}
          </div>

            {/* Contributions Section - Always show if there are contribution features */}
            {contributionFeatures.length > 0 && (
            <div className="relative">
              <button
                className={`flex items-center w-full p-2 rounded-md justify-between transition-colors ${
                  contributionFeatures.some(f => activePage === f.path)
                    ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
                onClick={() => setContributionsDropdownOpen((open) => !open)}
              >
                <span className="flex items-center">
                  <MessageSquare size={18} className="mr-2" />
                  Contributions
                </span>
                <ChevronDown size={16} className={`ml-2 transition-transform duration-200 ${contributionsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                contributionsDropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="mt-1 ml-6">
                  {contributionFeatures.map(feature => {
                    const isLocked = isFeatureLocked(feature);
                    const isActive = activePage === feature.path;
                    const isLoading = isFeaturesLoading && feature.apiFeature;
                    
                    const baseClasses = `block px-3 py-2 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white' 
                        : isLocked 
                          ? 'text-gray-400 cursor-not-allowed dark:text-gray-500' 
                          : isLoading
                            ? 'text-gray-500 opacity-75 dark:text-gray-400'
                            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`;

                    if (isLocked) {
                      return (
                        <div 
                          key={feature.code} 
                          className={baseClasses} 
                          onClick={() => toast.error(`Upgrade your plan to unlock ${feature.label}.`)}
                          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
                          onMouseLeave={hideTooltip}
                        >
                          <span className="flex items-center">
                            {feature.label}
                            <Lock size={12} className="ml-2 text-gray-400" />
                          </span>
                        </div>
                      );
                    }

                    if (isLoading) {
                      return (
                        <div key={feature.code} className={baseClasses} title="Loading feature availability...">
                          <span className="flex items-center">
                            {feature.label}
                            <div className="ml-2 w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link 
                        key={feature.code}
                        className={baseClasses}
                        to={feature.path}
                        onClick={() => handleFeatureClick(feature)}
                      >
                        {feature.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Loans Section - Always show if there are loan features */}
          {loanFeatures.length > 0 && (
            <div className="relative">
              <button
                className={`flex items-center w-full p-2 rounded-md justify-between transition-colors ${
                  loanFeatures.some(f => activePage === f.path)
                    ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
                onClick={() => setLoansDropdownOpen((open) => !open)}
              >
                <span className="flex items-center">
                  <Clipboard size={18} className="mr-2" />
                  Loans
                </span>
                <ChevronDown size={16} className={`ml-2 transition-transform duration-200 ${loansDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                loansDropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="mt-1 ml-6">
                  {loanFeatures.map(feature => {
                    const isLocked = isFeatureLocked(feature);
                    const isActive = activePage === feature.path;
                    const isLoading = isFeaturesLoading && feature.apiFeature;
                    
                    const baseClasses = `block px-3 py-2 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white' 
                        : isLocked 
                          ? 'text-gray-400 cursor-not-allowed dark:text-gray-500' 
                          : isLoading
                            ? 'text-gray-500 opacity-75 dark:text-gray-400'
                            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`;

                    if (isLocked) {
                      return (
                        <div 
                          key={feature.code} 
                          className={baseClasses} 
                          onClick={() => toast.error(`Upgrade your plan to unlock ${feature.label}.`)}
                          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
                          onMouseLeave={hideTooltip}
                        >
                          <span className="flex items-center">
                            {feature.label}
                            <Lock size={12} className="ml-2 text-gray-400" />
                          </span>
                        </div>
                      );
                    }

                    if (isLoading) {
                      return (
                        <div key={feature.code} className={baseClasses} title="Loading feature availability...">
                          <span className="flex items-center">
                            {feature.label}
                            <div className="ml-2 w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link 
                        key={feature.code}
                        className={baseClasses}
                        to={feature.path}
                        onClick={() => handleFeatureClick(feature)}
                      >
                        {feature.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Meetings Section - Always show if there are meeting features */}
          {meetingFeatures.length > 0 && (
            <div className="relative">
              <button
                className={`flex items-center w-full p-2 rounded-md justify-between transition-colors ${
                  meetingFeatures.some(f => activePage === f.path)
                    ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
                onClick={() => setMeetingsDropdownOpen((open) => !open)}
              >
                <span className="flex items-center">
                  <BookOpen size={18} className="mr-2" />
                  Meetings
                </span>
                <ChevronDown size={16} className={`ml-2 transition-transform duration-200 ${meetingsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                meetingsDropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="mt-1 ml-6">
                  {meetingFeatures.map(feature => {
                    const isLocked = isFeatureLocked(feature);
                    const isActive = activePage === feature.path;
                    const isLoading = isFeaturesLoading && feature.apiFeature;
                    
                    const baseClasses = `block px-3 py-2 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white' 
                        : isLocked 
                          ? 'text-gray-400 cursor-not-allowed dark:text-gray-500' 
                          : isLoading
                            ? 'text-gray-500 opacity-75 dark:text-gray-400'
                            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`;

                    if (isLocked) {
                      return (
                        <div 
                          key={feature.code} 
                          className={baseClasses} 
                          onClick={() => toast.error(`Upgrade your plan to unlock ${feature.label}.`)}
                          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
                          onMouseLeave={hideTooltip}
                        >
                          <span className="flex items-center">
                            {feature.label}
                            <Lock size={12} className="ml-2 text-gray-400" />
                          </span>
                        </div>
                      );
                    }

                    if (isLoading) {
                      return (
                        <div key={feature.code} className={baseClasses} title="Loading feature availability...">
                          <span className="flex items-center">
                            {feature.label}
                            <div className="ml-2 w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link 
                        key={feature.code}
                        className={baseClasses}
                        to={feature.path}
                        onClick={() => handleFeatureClick(feature)}
                      >
                        {feature.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

         {/* Admin Features - Always show if there are admin features */}
          {isAdmin && adminFeatures.length > 0 && (
            <div className="mt-4 mb-4">
              <p className="mb-2 text-xs font-bold text-gray-500 uppercase">Administration</p>
              {adminFeatures.map(feature => {
                // Handle dropdown features (like admin management)
                if (feature.hasDropdown) {
                  const isActive = feature.dropdownItems.some(item => activePage === item.path);
                  const isLocked = isFeatureLocked(feature);
                  const isLoading = isFeaturesLoading && feature.apiFeature;
                  
                  const baseClasses = `flex items-center w-full p-2 rounded-md justify-between ${
                    isActive 
                      ? 'bg-blue-400 text-white dark:bg-blue-600 dark:text-white dark:hover:text-white' 
                      : isLocked 
                        ? 'text-gray-400 cursor-not-allowed dark:text-gray-500' 
                        : isLoading
                          ? 'text-gray-500 opacity-75 dark:text-gray-300'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800 dark:hover:text-white'
                  }`;

                  if (isLocked) {
                    return (
                      <div key={feature.code} className="relative">
                        <div 
                          className={baseClasses} 
                          onClick={() => toast.error(`Upgrade your plan to unlock ${feature.label}.`)}
                          onMouseEnter={(e) => showTooltip(`Upgrade your plan to unlock ${feature.label}. Click the Upgrade button in the sidebar to learn more.`, e)}
                          onMouseLeave={hideTooltip}
                        >
                          <span className="flex items-center">
                            <feature.icon size={18} className="mr-2" />
                            {feature.label}
                          </span>
                          <Lock size={14} className="text-gray-400" />
                        </div>
                      </div>
                    );
                  }

                  if (isLoading) {
                    return (
                      <div key={feature.code} className="relative">
                        <div className={baseClasses} title="Loading feature availability...">
                          <span className="flex items-center">
                            <feature.icon size={18} className="mr-2" />
                            {feature.label}
                          </span>
                          <div className="w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={feature.code} className="relative">
                      <button
                        className={baseClasses}
                        onClick={() => toggleDropdown(feature.code)}
                      >
                        <span className="flex items-center">
                          <feature.icon size={18} className="mr-2 text-inherit" />
                          {feature.label}
                        </span>
                        <ChevronDown
                          size={16}
                          className={`ml-2 transition-transform duration-200 text-gray-500 dark:text-white ${
                            openDropdowns[feature.code] ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        openDropdowns[feature.code] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="mt-1 ml-6">
                          {feature.dropdownItems.map(dropdownItem => {
                            const currentFullPath = location.pathname + location.search;
                            const isDropdownItemActive = currentFullPath === dropdownItem.path || activePage === dropdownItem.path;
                            
                            return (
                              <Link 
                                key={dropdownItem.code}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                                  isDropdownItemActive
                                    ? 'font-medium text-blue-600 bg-blue-50 dark:bg-blue-600 dark:text-white dark:hover:text-white' 
                                    : 'text-gray-600 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-800 dark:hover:text-white'
                                }`}
                                to={dropdownItem.path}
                                onClick={() => {
                                  handleFeatureClick({ ...feature, path: dropdownItem.path });
                                }}
                              >
                                {dropdownItem.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Handle regular features
                return (
                  <div key={feature.code}>
                    {renderFeatureLink(feature)}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Settings Features */}
          <div className="mb-4">
            <p className="mb-2 text-xs font-bold text-gray-500 uppercase">Settings</p>
            {settingsFeatures.map(feature => (
              <div key={feature.code}>
                {feature.isAction ? renderFeatureButton(feature) : renderFeatureLink(feature)}
              </div>
            ))}
          </div>

          {/* Create New Group Button - Show but lock if not available */}
          <div className="mt-4">
            {availableFeatures.includes('multiple_groups_management') ? (
              <button
                className="px-4 py-2 w-full font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                onClick={() => setShowGroupModal(true)}
              >
                Create New Group
              </button>
            ) : (
              <button
                className="flex justify-center items-center px-4 py-2 w-full text-white bg-gray-400 rounded-md cursor-not-allowed"
                disabled
                title="Upgrade plan to create new groups"
              >
                <span>Create New Group</span>
                <Lock size={14} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="overflow-auto flex-1">
        {/* Top Navigation */}
        <div className="flex sticky top-0 z-10 gap-1 justify-between items-center px-2 py-2 bg-white border-b sm:gap-2 sm:px-4">
          <button
            className="flex-shrink-0 p-2.5 rounded-md lg:hidden hover:bg-gray-100"
            aria-label="Open menu"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu size={24} />
          </button>

          <div className="flex flex-1 items-center px-1 py-2 w-full max-w-md min-w-0">
             { !isProfileLoading && profile?.data?.groups?.length > 1 ? <Select
              id="groupId"
              value={
                {
                  value: profile.data.current_group.id,
                  label: profile.data.current_group.name,
                  id: profile.data.current_group.id
                }
              }
              onChange={(selectedOption) => handleSwitchGroupSubmit(selectedOption?.id)}
              options={profile.data.groups.map(group => ({
                value: group.id,
                label: group.name,
                id: group.id
              }))}
              className="w-full text-base"
              classNamePrefix="select"
              isSearchable={true}
              placeholder="Choose active group.."
              required
             />: (<div className="truncate">{profile?.data?.current_group?.name || ".."}</div>) }
          </div>

          <div className="flex flex-shrink-0 items-center">
            {/* theme switch */}
            <ThemeToggle/>
            {/* Notification Bell */}
            {(Number(profile?.data?.current_group?.is_house_management) !== 1) && <NotificationBell />}
            <div className="flex items-center ml-1 sm:mr-4 sm:ml-4">
            {!isProfileLoading && (
              <div className="sm:mr-4">
                {profile?.data?.current_group?.logo_full ? (
                  <img 
                    src={typeof profile.data.current_group.logo_full === 'string' && profile.data.current_group.logo_full.includes('http')
                      ? profile.data.current_group.logo_full
                      : `data:image/jpeg;base64,${profile.data.current_group.logo_full}`}
                    alt="Group Logo" 
                    className="object-contain w-auto h-8 max-w-[4rem] rounded sm:max-w-none"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : (
                  <div className="flex justify-center items-center w-8 h-8 font-medium text-white bg-blue-500 rounded-full">
                    {profile?.data?.name[0]}
                  </div>
                )}
              </div>
            )}
              <div className="hidden ml-2 md:block">
                <p className="text-sm font-medium">{profile?.data?.name}</p>
                <p className="text-xs text-gray-500">
                  {profile?.data?.is_group_owner
                    ? 'Super Admin'
                    : profile?.data?.role?.display_name || 'Member'}
                </p>
              </div>
            </div>
            

          </div>
        </div>
        
        {/* Automation Banner - above main content area only */}
        {showBanner && !profile?.data?.mpesa_credentials && (
          <div className="flex relative justify-center items-center py-3 pr-12 pl-4 w-full bg-green-100 border-b-2 border-green-400">
            <span className="flex-shrink-0 mr-2">
              <Globe size={20} className="inline text-green-600" />
            </span>
            <span className="text-sm font-medium text-green-900 md:text-base">
              You can <strong>automate contribution collection</strong> by attaching your Safaricom Paybill. This will automate the collection process in the platform.
              <span className="block mt-1 sm:inline sm:mt-0 sm:ml-4">
                <Link to="/settings" className="font-semibold text-green-700 underline hover:text-green-900">Go to Settings &gt; MPESA Integration</Link>
              </span>
            </span>
            <button
              className="absolute right-2 top-1/2 px-3 py-1 text-2xl font-bold text-green-900 -translate-y-1/2 hover:text-green-700 focus:outline-none"
              aria-label="Close banner"
              onClick={() => setShowBanner(false)}
            >
              &times;
            </button>
          </div>
        )}

        {/* SMS Quota Reminder Flashcard - Floating card in top-right */}
        {shouldShowSmsQuotaReminder && (
          <div className="fixed top-20 right-4 left-4 z-50 ml-auto max-w-sm animate-slide-in-right">
            <div className={`relative bg-white rounded-lg shadow-lg border-l-4 ${
              (testQuota || quota)?.is_exhausted 
                ? 'border-red-500' 
                : 'border-yellow-500'
            } p-4`}>
              <button
                onClick={handleDismissSmsQuotaReminder}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss reminder"
              >
                <X size={18} />
              </button>
              
              <div className="flex items-start pr-6">
                <div className={`flex-shrink-0 p-2 rounded-full ${
                  (testQuota || quota)?.is_exhausted 
                    ? 'bg-red-100' 
                    : 'bg-yellow-100'
                }`}>
                  {(testQuota || quota)?.is_exhausted ? (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  ) : (
                    <Battery className="w-5 h-5 text-yellow-600" />
                  )}
                </div>
                
                <div className="ml-3 flex-1">
                  <h3 className={`text-sm font-semibold ${
                    (testQuota || quota)?.is_exhausted 
                      ? 'text-red-900' 
                      : 'text-yellow-900'
                  }`}>
                    {(testQuota || quota)?.is_exhausted ? 'SMS Quota Exhausted' : 'Low SMS Quota'}
                  </h3>
                  <p className="mt-1 text-xs text-gray-600">
                    {(testQuota || quota)?.is_exhausted 
                      ? `You have ${(testQuota || quota)?.remaining || quota?.remaining || 0} SMS remaining. Recharge now to continue sending messages.`
                      : `You have ${(testQuota || quota)?.remaining || quota?.remaining || 0} SMS remaining. Consider recharging soon.`
                    }
                  </p>
                  <Link
                    to="/settings?tab=sms-quota&recharge=true"
                    className="mt-2 inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800"
                    onClick={handleDismissSmsQuotaReminder}
                  >
                    Recharge SMS &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Feature Announcement Banner */}
        <FeatureAnnouncementBanner />
        
        {/* Page Content */}
        <main className="overflow-y-auto flex-1 p-4">
          {!isProfileLoading && isSubscriptionExpired ? (
            <div className="flex flex-col items-center min-h-[70vh] justify-center gap-6">
              {isAdmin && !isPricingBannerDismissed && (
                <div className="relative bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 max-w-md w-full">
                  <button
                    onClick={handleDismissPricingBanner}
                    className="absolute top-4 right-4 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X size={18} />
                  </button>
                  <div className="flex items-start gap-3 pr-8">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-sm mb-1">
                        Advanced Plan pricing update
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        The Advanced Plan price has increased from KSh 500 to KSh 999 due to rising server management fees.
                        This takes effect in your next billing cycle. We've also retired the free plan — new groups now get
                        a 7-day trial period instead.{' '}
                        <a
                          href="https://twachanga.co.ke/pricing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                        >
                          View pricing details →
                        </a>
                      </p>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                        We've also released a new version of our mobile app.{' '}
                        <a
                          href="https://play.google.com/store/apps/details?id=co.ke.twachanga"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                        >
                          Get it on Google Play →
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="max-w-md text-center bg-white dark:bg-gray-800 rounded-2xl border shadow-sm p-8">
                <div className="inline-flex justify-center items-center mb-4 w-14 h-14 bg-red-50 dark:bg-red-950 rounded-full">
                  <Lock size={28} className="text-red-500" />
                </div>
                <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Access Restricted
                </h2>
                <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                  {profile?.data?.current_group?.is_trial
                    ? "Your group's free trial has ended."
                    : `Your group's ${currentPlanName || ''} Plan subscription has ended.`}
                  {' '}The system is locked until payment is made
                  {!isOnAdvanced && renewalAmount ? ` (KES ${renewalAmount}/mo)` : ''}.
                  {!isAdmin && ' Please contact your group admin to renew.'}
                </p>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setUpgradeTargetPlan('current');
                      setShowUpgradeModal(true);
                    }}
                    className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-sm transition-all duration-200 hover:from-blue-700 hover:to-purple-700"
                  >
                    Renew Now
                  </button>
                )}
              </div>
            </div>
          ) : (
            children
          )}
        </main>
      </div>

      {/* Create Group Modal (centered over main content) */}
      {showGroupModal && (
        <Modal isOpen={showGroupModal} onClose={() => setShowGroupModal(false)} title="Create New Group">
          <form onSubmit={handleGroupSubmit}>
            <div className="mb-4">
              <label className="block mb-1 text-sm font-bold text-gray-700">Group Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="name"
                value={groupForm.name}
                onChange={handleGroupChange}
                className="block px-3 py-2 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={groupForm.description}
                onChange={handleGroupChange}
                className="block px-3 py-2 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                rows={3}
              />
            </div>
            {groupError && <div className="mb-2 text-sm text-red-600">{groupError}</div>}
            <div className="flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                onClick={() => setShowGroupModal(false)}
                disabled={groupLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                disabled={groupLoading}
              >
                {groupLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        onComplete={() => {
          setShowOnboardingModal(false);
          toast.success('Welcome to Twachanga! Your group is ready to go! 🎉');
        }}
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <Modal 
          isOpen={showUpgradeModal && !isFeaturesLoading}
          onClose={() => {
            setShowUpgradeModal(false);
            if (pollingRef.current) clearInterval(pollingRef.current);
            setUpgradeForm({ phone_number: '', amount: targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT, description: '', duration_months: 1 });
            setUpgradeTargetPlan('current');
            setUpgradeSteps([
              { key: 'twachanga_start', label: 'Twachanga', status: 'idle' },
              { key: 'mpesa_init', label: 'M-Pesa', status: 'idle' },
              { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'idle' },
              { key: 'twachanga_return', label: 'Twachanga', status: 'idle' },
              { key: 'outcome', label: 'Success / Failed', status: 'idle' },
            ]);
            setUpgradeStatus('idle');
            setUpgradeMessage('');
            setUpgradeLoading(false);
          }} 
          title={isOnAdvanced ? 'Extend Your Plan' : 'Upgrade Your Plan'}
          size="md"
        >
          <form onSubmit={async (e) => {
            e.preventDefault();
            setUpgradeLoading(true);
            setUpgradeStatus('processing');
            setUpgradeMessage('Waiting for M-Pesa confirmation...');
            setUpgradeSteps([
              { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
              { key: 'mpesa_init', label: 'M-Pesa', status: 'active' },
              { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'active' },
              { key: 'twachanga_return', label: 'Twachanga', status: 'pending' },
              { key: 'outcome', label: 'Success / Failed', status: 'idle' },
            ]);
            
            try {
              // Normalize phone number to 254XXXXXXXXX
              let phoneNumber = (upgradeForm.phone_number || '')
                .toString()
                .trim()
                .replace(/[^0-9]/g, ''); // strip + and any non-digits

              if (phoneNumber.startsWith('254')) {
                // already in 254 format
              } else if (phoneNumber.startsWith('0')) {
                phoneNumber = '254' + phoneNumber.substring(1);
              } else if (phoneNumber.length === 9 && phoneNumber.startsWith('7')) {
                // e.g. 7XXXXXXXX -> 2547XXXXXXXX
                phoneNumber = '254' + phoneNumber;
              } else if (phoneNumber.length === 12 && phoneNumber.startsWith('254')) {
                // keep as is
              } else {
                // fallback: prepend 254 if missing
                if (!phoneNumber.startsWith('254')) {
                  phoneNumber = '254' + phoneNumber.replace(/^0+/, '');
                }
              }

              // Get current group and plan info
              const groupId = profile?.data?.current_group?.id;
              const planId = targetPlanId;
              // Renewing the same plan doesn't change plan_id, so capture the
              // expiry date beforehand and watch for it to move forward instead.
              const previousExpiry = profile?.data?.current_group?.subscription_expiry_date;

              if (!groupId || !planId) {
                toast.error('Unable to find group or plan. Please try again.');
                setUpgradeLoading(false);
                return;
              }

              const response = await api.post('/plans/upgrade', {
                phone_number: phoneNumber,
                amount: upgradeForm.amount,
                group_id: groupId,
                plan_id: planId,
                duration_months: upgradeForm.duration_months || 1
              });

              if (response.data.success) {
                if (response.data.requires_payment === false) {
                  setUpgradeStatus('success');
                  setUpgradeMessage('Plan extended successfully!');
                  setUpgradeSteps([
                    { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
                    { key: 'mpesa_init', label: 'M-Pesa', status: 'done' },
                    { key: 'enter_pin', label: 'Payment not required', status: 'done' },
                    { key: 'twachanga_return', label: 'Twachanga', status: 'done' },
                    { key: 'outcome', label: 'Success', status: 'success' },
                  ]);
                  setTimeout(() => {
                    setShowUpgradeModal(false);
                    setUpgradeForm({ phone_number: '', amount: targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT, description: '', duration_months: 1 });
                    setUpgradeTargetPlan('current');
                    setUpgradeStatus('idle');
                    setUpgradeMessage('');
                    setUpgradeLoading(false);
                    queryClient.invalidateQueries({ queryKey: ['profile'] });
                    toast.success('Subscription upgraded successfully.');
                  }, 2000);
                  return;
                }

                toast.success(response.data.message || 'Upgrade initiated. Check your M-PESA to complete payment.');
                // Poll profile until plan_id matches the target and the expiry date has moved forward (or timeout)
                let attempts = 0;
                if (pollingRef.current) clearInterval(pollingRef.current);
                pollingRef.current = setInterval(async () => {
                  attempts += 1;
                  const res = await refetchProfile();
                  const g = res?.data?.data?.current_group;
                  // Compare expiry dates as timestamps so format/timezone differences don't cause false negatives.
                  // When renewing the same plan, plan_id doesn't change - only the expiry moves forward.
                  const newExpiry = g?.subscription_expiry_date ? new Date(g.subscription_expiry_date).getTime() : 0;
                  const oldExpiry = previousExpiry ? new Date(previousExpiry).getTime() : 0;
                  const expiryMoved = newExpiry > 0 && newExpiry > oldExpiry;
                  const planMatches = g?.plan_id == planId; // == intentional: handles number/string mismatch
                  const isUpgraded = planMatches && expiryMoved;
                  if (isUpgraded) {
                    clearInterval(pollingRef.current);
                    setUpgradeStatus('success');
                    setUpgradeMessage('Payment received. Enjoy our services!');
                    setUpgradeSteps([
                      { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
                      { key: 'mpesa_init', label: 'M-Pesa', status: 'done' },
                      { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'done' },
                      { key: 'twachanga_return', label: 'Twachanga', status: 'done' },
                      { key: 'outcome', label: 'Success / Failed', status: 'success' },
                    ]);
                    // brief success hold before closing
                    setTimeout(() => {
                      setShowUpgradeModal(false);
                      setUpgradeForm({ phone_number: '', amount: targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT, description: '', duration_months: 1 });
                      setUpgradeTargetPlan('current');
                      setUpgradeStatus('idle');
                      setUpgradeMessage('');
                      setUpgradeLoading(false);
                      queryClient.invalidateQueries({ queryKey: ['profile'] });
                      toast.success('Subscription upgraded successfully.');
                    }, 2000);
                  } else if (attempts >= 40) { // ~120s @ 3s interval
                    clearInterval(pollingRef.current);
                    setUpgradeStatus('failed');
                    setUpgradeMessage('We did not receive confirmation in time. If you approved the prompt, please check later or contact support.');
                    setUpgradeSteps([
                      { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
                      { key: 'mpesa_init', label: 'M-Pesa', status: 'done' },
                      { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'done' },
                      { key: 'twachanga_return', label: 'Twachanga', status: 'done' },
                      { key: 'outcome', label: 'Success / Failed', status: 'failed' },
                    ]);
                    setUpgradeLoading(false);
                  }
                }, 3000);
              } else {
                setUpgradeStatus('failed');
                setUpgradeMessage(response.data.message || 'Failed to initiate upgrade. Please try again.');
                setUpgradeSteps([
                  { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
                  { key: 'mpesa_init', label: 'M-Pesa', status: 'done' },
                  { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'done' },
                  { key: 'twachanga_return', label: 'Twachanga', status: 'done' },
                  { key: 'outcome', label: 'Success / Failed', status: 'failed' },
                ]);
              }
            } catch (error) {
              console.log(error)
              const errorMessage = error?.response?.data?.message || 
                                 error?.response?.data?.error || 
                                 'Failed to initiate upgrade. Please try again.';
              setUpgradeStatus('failed');
              setUpgradeMessage(errorMessage);
              setUpgradeSteps([
                { key: 'twachanga_start', label: 'Twachanga', status: 'done' },
                { key: 'mpesa_init', label: 'M-Pesa', status: 'done' },
                { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'done' },
                { key: 'twachanga_return', label: 'Twachanga', status: 'done' },
                { key: 'outcome', label: 'Success / Failed', status: 'failed' },
              ]);
            } finally {
              // keep loading if processing; stop only on success/fail branch
              if (upgradeStatus === 'idle') setUpgradeLoading(false);
            }
          }}>
            {/* Visual progress - hidden by default, shown only after submit */}
            {upgradeStatus !== 'idle' && (
              <div className="mb-4">
                <div className="flex flex-col space-y-3">
                  {upgradeSteps.map((s, idx) => (
                    <div key={s.key} className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${s.status === 'success' || s.status === 'done' ? 'bg-green-600' : s.status === 'failed' ? 'bg-red-600' : s.status === 'active' || s.status === 'pending' ? 'bg-blue-600 animate-pulse' : 'bg-gray-300'}`}></div>
                      <span className={`text-sm ${s.status === 'failed' ? 'text-red-600' : s.status === 'active' || s.status === 'pending' ? 'text-blue-700' : 'text-gray-700'}`}>{s.label}</span>
                      {idx < upgradeSteps.length - 1 && (
                        <span className={`ml-2 ${s.status === 'success' || s.status === 'done' ? 'text-green-500' : s.status === 'failed' ? 'text-red-500' : s.status === 'active' || s.status === 'pending' ? 'text-blue-500' : 'text-gray-300'}`}>↓</span>
                      )}
                    </div>
                  ))}
                </div>
                {/* Loading spinner during processing */}
                {upgradeStatus === 'processing' && (
                  <div className="flex items-center mt-4 space-x-2 text-sm text-gray-600">
                    <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                    <span>Processing payment…</span>
                  </div>
                )}
              </div>
            )}

            {upgradeStatus === 'idle' && !isOnAdvanced && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Choose a plan
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setUpgradeTargetPlan('current')}
                  className={`p-3 text-left rounded-lg border-2 transition-colors ${
                    upgradeTargetPlan === 'current'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Continue on {currentPlanName || 'current plan'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">KES {renewalAmount || DEFAULT_CORE_PLAN_AMOUNT}/month</div>
                </button>
                <button
                  type="button"
                  onClick={() => setUpgradeTargetPlan('advanced')}
                  className={`p-3 text-left rounded-lg border-2 transition-colors ${
                    upgradeTargetPlan === 'advanced'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Upgrade to Advanced</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">KES {upgradeAmount || 999}/month</div>
                </button>
              </div>
            </div>
            )}

            {upgradeStatus === 'idle' && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone_number"
                value={upgradeForm.phone_number}
                onChange={(e) => setUpgradeForm(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="254700000000"
                className="block px-3 py-2 w-full bg-gray-50 rounded-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
              <small className="mt-1 text-xs text-gray-500">Enter your M-Pesa registered phone number</small>
            </div>
            )}

            {upgradeStatus === 'idle' && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={upgradeForm.amount}
                readOnly
                className="block px-3 py-2 w-full bg-gray-100 rounded-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg shadow-sm cursor-not-allowed"
                required
              />
              <small className="mt-1 text-xs text-gray-500">Upgrade amount (KES {upgradeForm.amount})</small>
            </div>
            )}

            {upgradeStatus === 'idle' && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Duration
              </label>
              <select
                className="block px-3 py-2 w-full bg-white rounded-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                value={upgradeForm.duration_months || 1}
                onChange={(e) => {
                  const months = Number(e.target.value || 1);
                  const base = targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT;
                  const d = (durations || []).find(x => x.months === months);
                  const discountPercent = d?.percentage_discount || 0;
                  const gross = base * months;
                  const discountAmount = Math.floor(gross * discountPercent / 100);
                  const total = gross - discountAmount;
                  setUpgradeForm(prev => ({ ...prev, duration_months: months, amount: total }));
                }}
              >
                {(durations && durations.length ? durations : [
                  { duration_key: 'monthly', months: 1 },
                  { duration_key: 'quarterly', months: 3 },
                  { duration_key: 'half-year', months: 6 },
                  { duration_key: 'annual', months: 12 },
                ]).map(d => (
                  <option key={d.months} value={d.months}>
                    {d.duration_key.replace('-', ' ')} ({d.months} {d.months === 1 ? 'month' : 'months'})
                    {d.percentage_discount && d.percentage_discount > 0 ? ` (−${d.percentage_discount}% discount)` : ''}
                  </option>
                ))}
              </select>
              <small className="mt-1 text-xs text-gray-500">
                {(() => {
                  const base = targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT;
                  const months = upgradeForm.duration_months || 1;
                  const d = (durations || []).find(x => x.months === months);
                  const p = d?.percentage_discount || 0;
                  const gross = base * months;
                  const disc = Math.floor(gross * p / 100);
                  const total = gross - disc;
                  return `Total = ${base} x ${months}${p ? ` − ${p}% (${disc})` : ''} = KES ${total.toLocaleString()}`;
                })()}
              </small>
            </div>
            )}

            {upgradeStatus === 'idle' && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={upgradeForm.description}
                onChange={(e) => setUpgradeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional: Add any notes or reference for this upgrade payment"
                className="block px-3 py-2 w-full bg-gray-50 rounded-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                rows={3}
              />
            </div>
            )}

            <div className="flex justify-end mt-6 space-x-3">
              <button
                type="button"
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                onClick={() => {
                  setShowUpgradeModal(false);
                  if (pollingRef.current) clearInterval(pollingRef.current);
                  setUpgradeForm({ phone_number: '', amount: targetBaseAmount || DEFAULT_CORE_PLAN_AMOUNT, description: '', duration_months: 1 });
                  setUpgradeTargetPlan('current');
                  setUpgradeSteps([
                    { key: 'twachanga_start', label: 'Twachanga', status: 'idle' },
                    { key: 'mpesa_init', label: 'M-Pesa', status: 'idle' },
                    { key: 'enter_pin', label: 'Please check your phone and enter PIN', status: 'idle' },
                    { key: 'twachanga_return', label: 'Twachanga', status: 'idle' },
                    { key: 'outcome', label: 'Success / Failed', status: 'idle' },
                  ]);
                  setUpgradeStatus('idle');
                  setUpgradeMessage('');
                  setUpgradeLoading(false);
                }}
                disabled={upgradeLoading || upgradeStatus === 'processing'}
              >
                Cancel
              </button>
              {upgradeStatus === 'idle' && (
                <button
                  type="submit"
                  disabled={upgradeLoading}
                  className="px-4 py-2 font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-md transition-all hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {upgradeLoading ? 'Processing...' : 'Proceed to Payment'}
                </button>
              )}
            </div>
            {upgradeStatus !== 'idle' && (
              <div className={`mt-3 text-sm ${upgradeStatus === 'failed' ? 'text-red-600' : 'text-green-700'}`}>
                {upgradeMessage}
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}